import type {

  IAccountCondensed,

  IAccountFull,

  IAccountPlan,

  IAccountHistory,

  IAccountTag,

  ICandidateAction,

  IContactLimits,

  IIndustry,

  IDailyActionsList,

  IWeeklyActionsList,

  IAvailableSPVoice,

  ISocialPromptSeriesReadWithStatus,

  ClientTierSignalTaxonomyRead,

  CampaignTierSignalTaxonomy,

} from "../Types/account";

import type { IAsset } from "../Types/assets";

import type { ICalendarAccountInsight } from "../Types/calendar";

import type { PriorityDefaults } from "../Types/notification";

import type { ISignalSourcesBatchRequest, ISignalSourcesBatchResponse } from "../Types/signalSourcesBatch";

import type { IUserCondensed } from "../Types/user";

import {

  TUTORIAL_AMC_CLIENT_ID,

  TUTORIAL_DEFAULT_CAMPAIGN_ID,

  TUTORIAL_DEMO_CONTACTS,

  TUTORIAL_DEMO_HERO_ACTION,

  TUTORIAL_DEMO_INSIGHTS,

  TUTORIAL_DEMO_PORTFOLIO,

  TUTORIAL_SCENARIO,

} from "../Constants/tutorialScenario";

import { TUTORIAL_DEMO_ACCOUNT_NUMERIC_ID } from "../Constants/tutorialDemoData";

import { isTutorialFixtureActive } from "../utils/tutorialFixtureState";

import { sanitizeTutorialData } from "../utils/sanitizeTutorialFixture";

import {
  addLocalDays,
  getMondayOfCalendarWeek,
  parseLocalDateKey,
  startOfLocalDay,
  toLocalDateKey,
} from "../utils/accountCalendarHelpers";



import dailyListFixture from "../fixtures/tutorial/daily-list.json";
import weeklyListFixture from "../fixtures/tutorial/weekly-list.json";

import portfolioAccountsFixture from "../fixtures/tutorial/portfolio-accounts.json";

import zayoAccountFixture from "../fixtures/tutorial/zayo-account.json";

import industriesFixture from "../fixtures/tutorial/industries.json";

import assetsFixture from "../fixtures/tutorial/assets.json";

import cellhireSocialPromptFixture from "../fixtures/tutorial/cellhire-social-prompt.json";

import cellhireContactFixture from "../fixtures/tutorial/cellhire-contact.json";

import zayoSocialSeriesFixture from "../fixtures/tutorial/zayo-social-series.json";

import zayoAccountPlanFixture from "../fixtures/tutorial/zayo-account-plan.json";

import signalSourcesBatchFixture from "../fixtures/tutorial/signal-sources-batch.json";

import accountBatchFixture from "../fixtures/tutorial/account-batch.json";

import accountLimitsFixture from "../fixtures/tutorial/account-limits.json";

import taxonomyFixture from "../fixtures/tutorial/taxonomy.json";

import priorityDefaultsFixture from "../fixtures/tutorial/priority-defaults.json";

import availableVoicesFixture from "../fixtures/tutorial/available-voices.json";

import usersFixture from "../fixtures/tutorial/users.json";

import salesLeadersFixture from "../fixtures/tutorial/sales-leaders.json";



function serveTutorialFixture<T>(data: T): T {

  return sanitizeTutorialData(data);

}



const HERO_ACTION_ID = TUTORIAL_DEMO_HERO_ACTION.action_id;

/** Cap tour hotlist fixtures to one table page (matches default page size). */
const TUTORIAL_HOTLIST_MAX_ROWS = 10;

const ZAYO_ACCOUNT_UUID = TUTORIAL_SCENARIO.primaryAccount.uuid;



type FixtureInsightRow = {

  id: number;

  account_id: string;

  type: ICalendarAccountInsight["type"];

  subtype: ICalendarAccountInsight["subtype"];

  link_text: string | null;

  link_url: string | null;

  link_image: string | null;

  date: string | null;

  notes: string[] | null;

  synopsis: string | null;

  short_summary?: string | null;

  relevance_score: number | null;

  ai_quality_score: number | null;

  recency_score: number | null;

  overall_score: number | null;

  created_at: string;

};



const ACCOUNT_METADATA_BY_UUID = new Map(

  TUTORIAL_DEMO_PORTFOLIO.map((account) => [

    account.uuid,

    {

      id: account.uuid,

      name: account.name,

      domain: account.domain,

      logo_image_url: account.logo_image_url,

    },

  ]),

);



function preferHeroThenLimit<T extends { action_id: string }>(

  items: T[],

  maxRows: number,

): T[] {

  const hero = items.find((item) => item.action_id === HERO_ACTION_ID);

  const rest = items.filter((item) => item.action_id !== HERO_ACTION_ID);

  const ordered = hero ? [hero, ...rest] : items;

  return ordered.slice(0, maxRows);

}



function reorderDailyList(): IDailyActionsList {

  const data = dailyListFixture as IDailyActionsList;

  return {

    ...data,

    items: preferHeroThenLimit(data.items, TUTORIAL_HOTLIST_MAX_ROWS),

  };

}



function trimWeeklyList(): IWeeklyActionsList {

  const data = weeklyListFixture as unknown as IWeeklyActionsList;

  const withDay = data.days.flatMap((day) =>

    day.items.map((item) => ({ item, date: day.date })),

  );

  const heroEntry = withDay.find((entry) => entry.item.action_id === HERO_ACTION_ID);

  const rest = withDay.filter((entry) => entry.item.action_id !== HERO_ACTION_ID);

  const limited = (heroEntry ? [heroEntry, ...rest] : withDay).slice(

    0,

    TUTORIAL_HOTLIST_MAX_ROWS,

  );

  const itemsByDate = new Map<string, (typeof data.days)[number]["items"]>();

  for (const day of data.days) itemsByDate.set(day.date, []);

  for (const entry of limited) {

    const bucket = itemsByDate.get(entry.date);

    if (bucket) bucket.push(entry.item);

  }

  return {

    ...data,

    days: data.days.map((day) => ({

      ...day,

      items: itemsByDate.get(day.date) ?? [],

    })),

    thought_leadership: [],

  };

}




function getZayoAccountMetadata() {

  const zayo = zayoAccountFixture as unknown as IAccountFull;

  return {

    id: ZAYO_ACCOUNT_UUID,

    name: zayo.name,

    domain: zayo.domain,

    logo_image_url: zayo.logo_image_url,

  };

}



function buildInsightIndex(): Map<number, FixtureInsightRow> {

  const index = new Map<number, FixtureInsightRow>();



  const addRows = (rows: FixtureInsightRow[]) => {

    for (const row of rows) {

      index.set(row.id, row);

    }

  };



  addRows((signalSourcesBatchFixture as { account_insights: FixtureInsightRow[] }).account_insights);

  addRows((accountBatchFixture as { account_insights: FixtureInsightRow[] }).account_insights);



  const zayo = zayoAccountFixture as unknown as IAccountFull;

  for (const row of zayo.insights ?? []) {

    index.set(row.id, {

      id: row.id,

      account_id: ZAYO_ACCOUNT_UUID,

      type: row.type,

      subtype: row.subtype ?? null,

      link_text: row.link_text ?? null,

      link_url: row.link_url ?? null,

      link_image: row.link_image ?? null,

      date: row.date ?? null,

      notes: row.notes ?? null,

      synopsis: row.synopsis ?? null,

      short_summary: row.short_summary ?? null,

      relevance_score: row.relevance_score ?? null,

      ai_quality_score: row.ai_quality_score ?? null,

      recency_score: row.recency_score ?? null,

      overall_score: row.overall_score ?? null,

      created_at: row.created_at,

    });

  }



  return index;

}



const INSIGHT_INDEX = buildInsightIndex();



function toCalendarAccountInsight(row: FixtureInsightRow): ICalendarAccountInsight {

  const accountMeta =

    ACCOUNT_METADATA_BY_UUID.get(row.account_id) ??

    (row.account_id === ZAYO_ACCOUNT_UUID ? getZayoAccountMetadata() : null) ?? {

      id: row.account_id,

      name: row.link_text?.split(" | ")[0] ?? "Account",

      domain: row.link_url ?? "",

      logo_image_url: null,

    };



  return {

    id: row.id,

    account_id: row.account_id,

    type: row.type,

    subtype: row.subtype,

    link_text: row.link_text,

    link_url: row.link_url,

    link_image: row.link_image,

    date: row.date,

    notes: row.notes,

    synopsis: row.synopsis,

    short_summary: row.short_summary ?? null,

    relevance_score: row.relevance_score,

    ai_quality_score: row.ai_quality_score,

    recency_score: row.recency_score,

    overall_score: row.overall_score,

    created_at: row.created_at,

    account: accountMeta as ICalendarAccountInsight["account"],

  };

}



function isTutorialZayoAccountId(accountId: string): boolean {

  return (

    String(accountId) === String(TUTORIAL_DEMO_ACCOUNT_NUMERIC_ID) ||

    accountId === ZAYO_ACCOUNT_UUID

  );

}



export function getTutorialDailyActionsList(): IDailyActionsList | null {

  if (!isTutorialFixtureActive()) return null;

  return serveTutorialFixture(reorderDailyList());

}



export function getTutorialWeeklyActionsList(): IWeeklyActionsList | null {

  if (!isTutorialFixtureActive()) return null;

  return serveTutorialFixture(trimWeeklyList());

}



export function getTutorialCondensedAccounts(clientId: string): IAccountCondensed[] | null {

  if (!isTutorialFixtureActive() || clientId !== TUTORIAL_AMC_CLIENT_ID) return null;

  return serveTutorialFixture(portfolioAccountsFixture as unknown as IAccountCondensed[]);

}



export function getTutorialFullAccount(accountId: string): IAccountFull | null {

  if (!isTutorialFixtureActive()) return null;

  if (!isTutorialZayoAccountId(accountId)) return null;

  return serveTutorialFixture({
    ...(zayoAccountFixture as unknown as IAccountFull),
    campaign_id: TUTORIAL_DEFAULT_CAMPAIGN_ID,
  });

}



export function getTutorialIndustries(clientId: string): IIndustry[] | null {

  if (!isTutorialFixtureActive() || clientId !== TUTORIAL_AMC_CLIENT_ID) return null;

  return serveTutorialFixture(industriesFixture as unknown as IIndustry[]);

}



export function getTutorialAssets(clientId: string): IAsset[] | null {

  if (!isTutorialFixtureActive() || clientId !== TUTORIAL_AMC_CLIENT_ID) return null;

  return serveTutorialFixture(assetsFixture as unknown as IAsset[]);

}



export function getTutorialAccountInsight(insightId: string): ICalendarAccountInsight | null {

  if (!isTutorialFixtureActive()) return null;

  const row = INSIGHT_INDEX.get(Number(insightId));

  if (!row) return null;

  return serveTutorialFixture(toCalendarAccountInsight(row));

}



export function getTutorialSocialPrompt(promptId: number) {

  if (!isTutorialFixtureActive()) return null;

  if (promptId === TUTORIAL_DEMO_HERO_ACTION.prompt_id) {

    return serveTutorialFixture(cellhireSocialPromptFixture);

  }

  return null;

}



export function getTutorialContactIntelligence(contactId: string | number) {

  if (!isTutorialFixtureActive()) return null;

  if (String(contactId) === String(TUTORIAL_DEMO_HERO_ACTION.to_contact_id)) {

    return serveTutorialFixture(cellhireContactFixture);

  }

  return null;

}



export function getTutorialSocialPromptSeries(seriesId: string) {

  if (!isTutorialFixtureActive()) return null;

  const series = (zayoSocialSeriesFixture as Array<{ id: string }>).find(

    (row) => row.id === seriesId,

  );

  const resolved = series ?? (zayoSocialSeriesFixture as unknown[])[0] ?? null;

  return resolved ? serveTutorialFixture(resolved) : null;

}



/** Series list for the Zayo tour insight used by the App Guide outreach workspace. */
export function getTutorialSocialPromptsForInsight(
  insightId: string,
  category: string,
): ISocialPromptSeriesReadWithStatus[] | null {
  if (!isTutorialFixtureActive()) return null;

  const insightNumeric = Number(insightId);
  if (
    category === "ACCOUNT" &&
    insightNumeric === TUTORIAL_DEMO_INSIGHTS[0].id
  ) {
    return serveTutorialFixture(
      zayoSocialSeriesFixture as unknown as ISocialPromptSeriesReadWithStatus[],
    );
  }

  return null;
}



/** Local-day delta between two YYYY-MM-DD keys. */
function localDateKeyDeltaDays(fromKey: string, toKey: string): number {
  const from = parseLocalDateKey(fromKey).getTime();
  const to = parseLocalDateKey(toKey).getTime();
  return Math.round((to - from) / 86_400_000);
}

function shiftIsoDateTimeByDays(
  iso: string | null | undefined,
  deltaDays: number,
): string | null {
  if (iso == null || iso === "") return iso ?? null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return d.toISOString();
}

type TutorialPlanItem = ICandidateAction & {
  committed_date?: string | null;
  past_due_since?: string | null;
  completed_at?: string | null;
};

function shiftPlanItemToDateKey(
  item: TutorialPlanItem,
  targetDateKey: string,
): TutorialPlanItem {
  const sourceKey =
    item.suggested_date ??
    (item.suggested_at ? toLocalDateKey(new Date(item.suggested_at)) : null) ??
    item.committed_date ??
    null;
  const deltaDays = sourceKey
    ? localDateKeyDeltaDays(sourceKey, targetDateKey)
    : 0;

  const shiftKey = (key: string | null | undefined): string | null => {
    if (key == null || key === "") return key ?? null;
    return toLocalDateKey(addLocalDays(parseLocalDateKey(key), deltaDays));
  };

  const series = item.series_context;
  const metadata = (item.metadata ?? null) as Record<string, unknown> | null;

  return {
    ...item,
    suggested_date: targetDateKey,
    committed_date: item.committed_date
      ? shiftKey(item.committed_date)
      : targetDateKey,
    suggested_at:
      shiftIsoDateTimeByDays(item.suggested_at, deltaDays) ?? item.suggested_at,
    past_due_since: shiftIsoDateTimeByDays(item.past_due_since, deltaDays),
    completed_at: shiftIsoDateTimeByDays(item.completed_at, deltaDays),
    approved_at: shiftIsoDateTimeByDays(item.approved_at, deltaDays),
    metadata: metadata
      ? {
          ...metadata,
          insight_occurred_at: shiftIsoDateTimeByDays(
            typeof metadata.insight_occurred_at === "string"
              ? metadata.insight_occurred_at
              : null,
            deltaDays,
          ),
        }
      : metadata,
    series_context: series
      ? {
          ...series,
          previous_steps: (series.previous_steps ?? []).map((step) => ({
            ...step,
            suggested_date: shiftKey(step.suggested_date),
          })),
          next_steps: (series.next_steps ?? []).map((step) => ({
            ...step,
            suggested_date: shiftKey(step.suggested_date),
          })),
        }
      : series,
  };
}

/**
 * Spread tour plan items across the current Mon–Sun week (today first) so the
 * account calendar list/month views look fully populated during the App Guide.
 */
function populateTutorialAccountPlanForCurrentWeek(
  plan: IAccountPlan,
  now: Date = new Date(),
): IAccountPlan {
  const monday = getMondayOfCalendarWeek(startOfLocalDay(now));
  const weekKeys = Array.from({ length: 7 }, (_, i) =>
    toLocalDateKey(addLocalDays(monday, i)),
  );
  const todayKey = toLocalDateKey(startOfLocalDay(now));
  const assignOrder = [
    todayKey,
    ...weekKeys.filter((key) => key !== todayKey),
  ];

  return {
    ...plan,
    items: plan.items.map((item, index) =>
      shiftPlanItemToDateKey(
        item as TutorialPlanItem,
        assignOrder[index % assignOrder.length],
      ),
    ),
  };
}

export function getTutorialAccountPlan(accountId: string): IAccountPlan | null {

  if (!isTutorialFixtureActive()) return null;

  if (!isTutorialZayoAccountId(accountId)) return null;

  const plan = populateTutorialAccountPlanForCurrentWeek(
    zayoAccountPlanFixture as unknown as IAccountPlan,
  );

  return serveTutorialFixture(plan);

}



export function getTutorialAccountHistory(accountId: string): IAccountHistory | null {

  if (!isTutorialFixtureActive()) return null;

  if (!isTutorialZayoAccountId(accountId)) return null;

  return { items: [] };

}



export function getTutorialAccountTags(accountId: string | number): IAccountTag[] | null {

  if (!isTutorialFixtureActive()) return null;

  if (!isTutorialZayoAccountId(String(accountId))) return null;

  return [];

}



export function getTutorialContactLimits(_accountId: string): IContactLimits | null {

  if (!isTutorialFixtureActive()) return null;

  return accountLimitsFixture as IContactLimits;

}



export function getTutorialClientTaxonomy(clientId: string): ClientTierSignalTaxonomyRead | null {

  if (!isTutorialFixtureActive() || clientId !== TUTORIAL_AMC_CLIENT_ID) return null;

  return taxonomyFixture as ClientTierSignalTaxonomyRead;

}



export function getTutorialCampaignTaxonomy(
  campaignId: string
): CampaignTierSignalTaxonomy {

  if (!isTutorialFixtureActive() || campaignId !== TUTORIAL_DEFAULT_CAMPAIGN_ID) return null;

  return (taxonomyFixture as ClientTierSignalTaxonomyRead).tier_signal_taxonomy;

}



export function getTutorialPriorityDefaults(): PriorityDefaults | null {

  if (!isTutorialFixtureActive()) return null;

  return priorityDefaultsFixture as PriorityDefaults;

}



export function getTutorialAvailableSPVoices(): IAvailableSPVoice[] | null {

  if (!isTutorialFixtureActive()) return null;

  return availableVoicesFixture as IAvailableSPVoice[];

}



export function getTutorialUsersPerClient(clientId: string): IUserCondensed[] | null {

  if (!isTutorialFixtureActive() || clientId !== TUTORIAL_AMC_CLIENT_ID) return null;

  return serveTutorialFixture(usersFixture as IUserCondensed[]);

}



export function getTutorialSalesLeaders(clientId: string): IUserCondensed[] | null {

  if (!isTutorialFixtureActive() || clientId !== TUTORIAL_AMC_CLIENT_ID) return null;

  return serveTutorialFixture(salesLeadersFixture as IUserCondensed[]);

}



export function getTutorialSignalSourcesBatch(

  body: ISignalSourcesBatchRequest,

): ISignalSourcesBatchResponse | null {

  if (!isTutorialFixtureActive()) return null;



  const batch = signalSourcesBatchFixture as {

    account_insights: FixtureInsightRow[];

    industry_insights: ISignalSourcesBatchResponse["industry_insights"];

    contacts: ISignalSourcesBatchResponse["contacts"];

    assets: ISignalSourcesBatchResponse["assets"];

  };



  const requestedAccountIds = new Set(body.account_insight_ids ?? []);

  const requestedIndustryIds = new Set(body.industry_insight_ids ?? []);

  const requestedContactIds = new Set(body.contact_intelligence_ids ?? []);

  const requestedAssetIds = new Set(body.asset_ids ?? []);



  // Prefer the merged insight index (batch dump + Zayo account) so account-plan
  // list cards resolve headlines for insight ids that only exist on the account.
  const insightIndex = buildInsightIndex();
  const account_insights = [...requestedAccountIds]
    .map((id) => insightIndex.get(id))
    .filter((row): row is FixtureInsightRow => row != null)
    .map((row) => ({
      id: row.id,
      link_text: row.link_text,
      link_url: row.link_url,
      synopsis: row.synopsis,
      short_summary: row.short_summary ?? null,
      notes: row.notes,
      date: row.date,
    }));



  const industry_insights = (batch.industry_insights ?? []).filter((row) =>

    requestedIndustryIds.has(row.id),

  );



  const contacts = [...(batch.contacts ?? [])];

  if (requestedContactIds.has(TUTORIAL_DEMO_HERO_ACTION.to_contact_id)) {

    const contact = cellhireContactFixture as {

      id: number;

      name: string;

      title?: string;

      linkedin_url?: string;

      summary?: string[] | null;

    };

    if (!contacts.some((row) => row.id === contact.id)) {

      contacts.push({

        id: contact.id,

        name: contact.name,

        title: contact.title ?? null,

        linkedin_url: contact.linkedin_url ?? null,

        summary: contact.summary ?? null,

      });

    }

  }

  for (const demoContact of TUTORIAL_DEMO_CONTACTS) {
    if (!requestedContactIds.has(demoContact.id)) continue;
    if (contacts.some((row) => row.id === demoContact.id)) continue;
    contacts.push({
      id: demoContact.id,
      name: demoContact.name,
      title: demoContact.title,
      linkedin_url: null,
      summary: null,
    });
  }



  const assets = (batch.assets ?? []).filter((row) => requestedAssetIds.has(row.id));



  return serveTutorialFixture({

    account_insights,

    industry_insights,

    contacts: contacts.filter((row) => requestedContactIds.has(row.id)),

    assets,

  });

}



export function isTutorialHeroActionId(actionId: string): boolean {

  return actionId === HERO_ACTION_ID;

}


